import { useEffect, useCallback, useState, useReducer } from "react";
//import { useLocation } from "react-router-dom";
import { useAuthContext } from "context/AuthContext";
import { useDocument } from "hooks/useDocument";
//import { useAbac } from "react-abac";
import { Permission } from "models/abac";

const collectionPath = "settings";
const documentPath = "month36_timepoint";

const initialState = {
  data: null,
  isPending: false,
  error: null,
  success: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "DISMISS":
      return {
        isPending: false,
        data: null,
        success: null,
        error: null,
      };
    case "IS_PENDING":
      return {
        isPending: true,
        data: null,
        success: null,
        error: null,
      };
    case "INITIAL":
      return {
        isPending: false,
        data: action.payload,
        success: null,
        error: null,
      };
    case "UPDATED_SETTINGS":
      return {
        isPending: false,
        data: action.payload,
        success: `Cutoff settings updated successfully.`,
        error: null,
      };
    case "ERROR":
      return {
        isPending: false,
        data: null,
        success: null,
        error: action.error,
      };
    default:
      return state;
  }
};

export const useCalculatorSettingsManager = () => {
  const [response, dispatch] = useReducer(reducer, initialState);
  const [isUnmounted, setIsUnmounted] = useState(false);
  const { user } = useAuthContext();
  //const { userHasPermissions } = useAbac();
  const { retrieveDoc, updateDoc, serverTimestamp } = useDocument();

  //const { pathname } = useLocation();

  const dispatchIfNotUnmounted = useCallback(
    (action) => {
      if (!isUnmounted) {
        dispatch(action);
      }
    },
    [isUnmounted]
  );

  const dispatchDismiss = useCallback(
    () => dispatchIfNotUnmounted({ type: "DISMISS" }),
    [dispatchIfNotUnmounted]
  );

  const dispatchError = useCallback(
    (err) => {
      console.error(err);
      if (
        ![
          "PermissionDeniedError",
          "OperationInvalidError",
          "CustomerInvalidError",
        ].includes(err.name)
      ) {
        err.message = "The operation couldn't be completed";
        err.name = "OperationIncompleteError";
        // TODO: send error stack to server
      }
      dispatchIfNotUnmounted({
        type: "ERROR",
        error: err,
      });
    },
    [dispatchIfNotUnmounted]
  );

  // TODO: Refactor to DAO Layer
  /*const toPersistenceValue = useCallback(
    (document) => {
      try {
        if (document.signatureAttachments) {
          document.signatureAttachments = document.signatureAttachments.map(
            (element) => {
              const { attachmentName, attachmentPath, attachmentURL } = element;
              return { attachmentName, attachmentPath, attachmentURL };
            }
          );
        }

        return document;
      } catch (err) {
        console.error(err);
        dispatchError(err);
      }
    },
    [dispatchError]
  );*/

  // TODO: Refactor to DAO Layer
  /*const toPresentationValue = useCallback(
    (data) => {
      try {
        // nothing to convert at this moment

        return data;
      } catch (err) {
        console.error(err);
        dispatchError(err);
      }
    },
    [dispatchError]
  );*/

  const validateOperation = useCallback(async () => {
    try {
      dispatchIfNotUnmounted({ type: "IS_PENDING" });
      let operationInvalidError = new Error(
        "Invalid Operation. You are not allowed to carry out this activity."
      );
      operationInvalidError.name = "OperationInvalidError";

      /*if (!pathname.includes("/settings/setcusumsettings")) {
        throw operationInvalidError;
      }*/

      const settings36mTimePoint = await retrieveDoc(
        collectionPath,
        documentPath
      );

      dispatchIfNotUnmounted({
        type: "INITIAL",
        payload: settings36mTimePoint.data,
      });
    } catch (err) {
      dispatchError(err);
    }
  }, [dispatchIfNotUnmounted, /*pathname,*/ dispatchError, retrieveDoc]);

  useEffect(() => {
    try {
      validateOperation();
    } catch (err) {
      dispatchError(err);
    }
    return () => {
      setIsUnmounted(true);
    };
  }, [dispatchError, validateOperation]);

  //let modeTitle = "Manage Cusum Settings";
  //let modeSubmit = "update";
  //let modeFieldDisabled = false;
  let modePermission = Permission.VIEW_ADMIN_DASHBOARD;
  //let modeValidation = validations;

  const submitEdit = async (values) => {
    try {
      dispatchIfNotUnmounted({ type: "IS_PENDING" });
      try {
        const updatedDoc = await updateDoc(collectionPath, documentPath, {
          ASQ3Communication_CutoffLB: values.ASQ3Communication_CutoffLB,
          ASQ3Communication_CutoffUB: values.ASQ3Communication_CutoffUB,
          ASQ3GrossMotor_CutoffLB: values.ASQ3GrossMotor_CutoffLB,
          ASQ3GrossMotor_CutoffUB: values.ASQ3GrossMotor_CutoffUB,
          ASQ3FineMotor_CutoffLB: values.ASQ3FineMotor_CutoffLB,
          ASQ3FineMotor_CutoffUB: values.ASQ3FineMotor_CutoffUB,
          ASQ3ProblemSolving_CutoffLB: values.ASQ3ProblemSolving_CutoffLB,
          ASQ3ProblemSolving_CutoffUB: values.ASQ3ProblemSolving_CutoffUB,
          ASQ3PersonalSocial_CutoffLB: values.ASQ3PersonalSocial_CutoffLB,
          ASQ3PersonalSocial_CutoffUB: values.ASQ3PersonalSocial_CutoffUB,
          ASQ3_SE2_CutoffLB: values.ASQ3_SE2_CutoffLB,
          ASQ3_SE2_CutoffUB: values.ASQ3_SE2_CutoffUB,
          SDQ_Total_CutoffB1: values.SDQ_Total_CutoffB1,
          SDQ_Total_CutoffB2: values.SDQ_Total_CutoffB2,
          SDQ_Total_CutoffB3: values.SDQ_Total_CutoffB3,
          SDQ_EmotionalProb_CutoffB1: values.SDQ_EmotionalProb_CutoffB1,
          SDQ_EmotionalProb_CutoffB2: values.SDQ_EmotionalProb_CutoffB2,
          SDQ_EmotionalProb_CutoffB3: values.SDQ_EmotionalProb_CutoffB3,
          SDQ_ConductProb_CutoffB1: values.SDQ_ConductProb_CutoffB1,
          SDQ_ConductProb_CutoffB2: values.SDQ_ConductProb_CutoffB2,
          SDQ_ConductProb_CutoffB3: values.SDQ_ConductProb_CutoffB3,
          SDQ_Hyperactivity_CutoffB1: values.SDQ_Hyperactivity_CutoffB1,
          SDQ_Hyperactivity_CutoffB2: values.SDQ_Hyperactivity_CutoffB2,
          SDQ_Hyperactivity_CutoffB3: values.SDQ_Hyperactivity_CutoffB3,
          SDQ_PeerProb_CutoffB1: values.SDQ_PeerProb_CutoffB1,
          SDQ_PeerProb_CutoffB2: values.SDQ_PeerProb_CutoffB2,
          SDQ_PeerProb_CutoffB3: values.SDQ_PeerProb_CutoffB3,
          SDQ_Prosocial_CutoffB1: values.SDQ_Prosocial_CutoffB1,
          SDQ_Prosocial_CutoffB2: values.SDQ_Prosocial_CutoffB2,
          SDQ_Prosocial_CutoffB3: values.SDQ_Prosocial_CutoffB3,
          modifiedAt: serverTimestamp(),
          modifiedBy: user.uid,
        });

        dispatchIfNotUnmounted({
          type: "UPDATED_SETTINGS",
          payload: updatedDoc.data,
        });
      } catch (err) {
        dispatchError(err);
      }
    } catch (err) {
      dispatchError(err);
    }
  };

  return {
    //modeTitle,
    //modeSubmit,
    //modeFieldDisabled,
    modePermission,
    //modeValidation,
    submitEdit,
    response,
    dispatchDismiss,
    dispatchError,
  };
};
